import clsx from 'clsx';
import Tooltip from 'components/layout/tooltip';
import { fuelUnavailableReason } from 'utils/composition-fuel-availability';
import {
  fuelActionForSide,
  fuelPickerLabelForSide,
  selectedFuelForSide
} from 'utils/composition-side';
import { fuelOptionsForSide } from 'utils/component-picker-options';
import { isCompositionPickReachable } from 'utils/system-composition';
import {
  CompositionIndex,
  CompositionPickAction,
  CompositionPicks,
  CompositionSide
} from 'utils/system-composition-types';

type WantFuelPickerProps = {
  side: CompositionSide;
  picks: CompositionPicks;
  index: CompositionIndex;
  onPick: (action: CompositionPickAction) => void;
};

// The furnace-fuel pill row of the want component picker (ported from the
// system-selector-frontend "System you want" stage). Radio semantics with
// toggle-off; fuels no type on this side can ever run on are not offered
// at all, and fuels the current picks can't reach render disabled. Each
// column of the picker asks it for its own side.
//
// Once the composition has no combustion side (air handler, modular
// blower) the three combustion fuels go dead and say why; Electric stays
// live, since that is the honest answer for those.
export default function WantFuelPicker({
  side,
  picks,
  index,
  onPick
}: WantFuelPickerProps) {
  const selectedFuel = selectedFuelForSide(picks, side);
  const options = fuelOptionsForSide(index, side, selectedFuel);
  if (options.length === 0) {
    return null;
  }
  return (
    <div>
      <span className='basis-full text-[10.5px] font-bold uppercase tracking-widest text-gray-400'>
        Fuel type
      </span>
      <div
        role='radiogroup'
        aria-label={fuelPickerLabelForSide(side)}
        className='mt-1 flex flex-wrap items-center gap-1.5'
      >
        {options.map((fuel) => {
          const selected = selectedFuel === fuel.value;
          const action = fuelActionForSide(side, fuel.value);
          const unavailableReason = fuelUnavailableReason(
            picks,
            fuel.value,
            side
          );
          const enabled =
            unavailableReason === null &&
            (selected || isCompositionPickReachable(picks, action, index));
          return (
            <span
              key={fuel.value}
              className={clsx(
                'relative inline-flex',
                !enabled && 'cursor-not-allowed'
              )}
            >
              <button
                type='button'
                role='radio'
                aria-checked={selected}
                disabled={!enabled}
                onClick={() => onPick(action)}
                className={clsx(
                  'rounded-lg border px-3 py-1.5 text-center text-xs leading-snug',
                  selected
                    ? 'border-blue-600 bg-blue-50 font-semibold text-blue-800'
                    : 'border-gray-300 bg-white text-gray-700',
                  enabled ? 'cursor-pointer' : 'cursor-not-allowed opacity-50',
                  enabled && !selected && 'hover:border-gray-400'
                )}
              >
                {fuel.label}
              </button>
              {!enabled && (
                <span className='absolute inset-0 z-10 cursor-not-allowed'>
                  <Tooltip
                    text={
                      unavailableReason ??
                      'Not available with your other selections'
                    }
                  />
                </span>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}
